import { useState, useEffect } from "react";

// Gallery images from installations folder
const START = 1;
const COUNT = 20;
const GALLERY_IMAGES = Array.from({ length: COUNT }, (_, i) => `/instalatii/IMG-20251020-WA${String(START + i).padStart(4, '0')}.jpg`);

export default function Home() {
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox({ open: false, index: 0 });
      if (e.key === "ArrowLeft")
        setLightbox((s) => ({ ...s, index: (s.index - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length }));
      if (e.key === "ArrowRight")
        setLightbox((s) => ({ ...s, index: (s.index + 1) % GALLERY_IMAGES.length }));
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox.open]);

  return (
    <main className="bg-gradient-to-b from-sky-50 via-white to-white text-slate-900">
      {/* Hero */}
      <section id="home" className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-10"
          aria-hidden="true"
          style={{
            backgroundImage: "url('/instalatii/IMG-20251020-WA0001.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-14 md:grid-cols-2 md:items-center md:py-20">
          <div>
            <span className="mb-3 inline-block rounded-full border border-sky-200 bg-white/70 px-3 py-1 text-xs font-medium text-sky-700">
              Instalații Sanitare Profesionale
            </span>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              Instalații Sanitare Arad - Centrale Termice, Pompe de Căldură
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-700">
              Servicii complete de instalații sanitare și termice în Arad: centrale termice, pompe de căldură, 
              încălzire în pardoseală, instalații solare. Peste 25 de ani de experiență în domeniu.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#servicii"
                className="rounded-xl bg-sky-600 px-5 py-3 text-white shadow-sm transition hover:bg-sky-700"
              >
                Vezi serviciile
              </a>
              <a
                href="tel:+40744476911"
                className="rounded-xl border border-sky-200 bg-white px-5 py-3 font-medium text-sky-700 shadow-sm transition hover:bg-sky-50"
              >
                Sună acum
              </a>
            </div>
          </div>

          {/* Hero gallery */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <img src="/instalatii/IMG-20251020-WA0001.jpg" alt="Instalații sanitare profesionale" className="aspect-[3/4] w-full rounded-2xl object-cover shadow" />
            <img src="/instalatii/IMG-20251020-WA0005.jpg" alt="Montaj centrale termice" className="col-span-2 aspect-video w-full rounded-2xl object-cover shadow" />
            <img src="/instalatii/IMG-20251020-WA0010.jpg" alt="Instalații baie complete" className="col-span-2 aspect-video w-full rounded-2xl object-cover shadow" />
            <img src="/instalatii/IMG-20251020-WA0015.jpg" alt="Radiatoare și țevi" className="aspect-[3/4] w-full rounded-2xl object-cover shadow" />
          </div>
        </div>
      </section>

      {/* Servicii */}
      <section id="servicii" className="scroll-mt-28">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
          <div className="rounded-3xl border bg-white p-6 shadow-sm md:p-8">
            <h2 className="text-2xl font-bold md:text-3xl">Servicii Complete de Instalații</h2>
            <p className="mt-2 text-slate-700">Oferim soluții profesionale pentru toate tipurile de instalații sanitare, termice și hidraulice în Arad și împrejurimi.</p>

            <div className="mx-auto grid max-w-6xl gap-6 px-4 py-10 md:grid-cols-2 md:py-14">
              {/* CENTRALE TERMICE */}
              <div id="centrale" className="rounded-2xl border bg-white p-6 shadow-sm">
                <HeaderWithIcon icon={<IconBoiler />} title="CENTRALE TERMICE" />
                <ul className="mt-5 grid gap-2 text-slate-800">
                  <Bullet>Centrale termice pe gaz</Bullet>
                  <Bullet>Centrale pe lemn</Bullet>
                  <Bullet>Centrale pe peleți</Bullet>
                  <Bullet>Întreținere și service</Bullet>
                </ul>
              </div>

              {/* POMPE DE CĂLDURĂ */}
              <div id="pompe" className="rounded-2xl border bg-white p-6 shadow-sm">
                <HeaderWithIcon icon={<IconHeatPump />} title="POMPE DE CĂLDURĂ" />
                <ul className="mt-5 grid gap-2 text-slate-800">
                  <Bullet>Montaj pompe de căldură aer-apă</Bullet>
                  <Bullet>Pompe de căldură sol-apă</Bullet>
                  <Bullet>Consultanță și dimensionare</Bullet>
                  <Bullet>Service și întreținere</Bullet>
                </ul>
              </div>

              {/* ÎNCĂLZIRE */}
              <div id="incalzire" className="rounded-2xl border bg-white p-6 shadow-sm">
                <HeaderWithIcon icon={<IconRadiator />} title="SISTEME DE ÎNCĂLZIRE" />
                <ul className="mt-5 grid gap-2 text-slate-800">
                  <Bullet>Încălzire în pardoseală</Bullet>
                  <Bullet>Montaj radiatoare</Bullet>
                  <Bullet>Echilibrare sistem</Bullet>
                  <Bullet>Modernizare instalații existente</Bullet>
                </ul>
              </div>

              {/* INSTALAȚII SOLARE */}
              <div id="solare" className="rounded-2xl border bg-white p-6 shadow-sm">
                <HeaderWithIcon icon={<IconSolar />} title="INSTALAȚII SOLARE" />
                <ul className="mt-5 grid gap-2 text-slate-800">
                  <Bullet>Instalații solare presurizate</Bullet>
                  <Bullet>Instalații solare nepresurizate</Bullet>
                  <Bullet>Panouri solare termice</Bullet>
                  <Bullet>Integrare cu sistemul existent</Bullet>
                </ul>
              </div>

              {/* INSTALAȚII SANITARE */}
              <div id="sanitare" className="rounded-2xl border bg-white p-6 shadow-sm">
                <HeaderWithIcon icon={<IconBathroom />} title="INSTALAȚII SANITARE" />
                <ul className="mt-5 grid gap-2 text-slate-800">
                  <Bullet>Instalații baie complete</Bullet>
                  <Bullet>Canalizare</Bullet>
                  <Bullet>Țevi apă caldă și rece</Bullet>
                  <Bullet>Montaj obiecte sanitare</Bullet>
                </ul>
              </div>

              {/* INSTALAȚII HIDRAULICE */}
              <div id="hidraulice" className="rounded-2xl border bg-white p-6 shadow-sm">
                <HeaderWithIcon icon={<IconTools />} title="INSTALAȚII HIDRAULICE" />
                <ul className="mt-5 grid gap-2 text-slate-800">
                  <Bullet>Toate tipurile de instalații hidraulice</Bullet>
                  <Bullet>Reparații și intervenții</Bullet>
                  <Bullet>Modernizări</Bullet>
                  <Bullet>Consultanță tehnică</Bullet>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Galerie */}
      <section id="galerie" className="scroll-mt-28 border-t bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
          <h2 className="text-xl font-semibold md:text-2xl">Lucrări Realizate</h2>
          <p className="mt-1 text-slate-700">Exemple de proiecte finalizate. Click pe imagine pentru detalii.</p>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {GALLERY_IMAGES.map((src, i) => (
              <button
                key={src}
                type="button"
                onClick={() => setLightbox({ open: true, index: i })}
                className="group block overflow-hidden rounded-lg border border-slate-200/70 bg-white"
                aria-label={`Mărește lucrarea ${i + 1}`}
              >
                <img
                  src={src}
                  alt={`Instalație ${i + 1}`}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                />
              </button>
            ))}
          </div>

          <div className="mt-6 text-center">
            <a
              href="/instalatii"
              className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-5 py-3 text-white shadow-sm transition hover:bg-sky-700"
            >
              Vezi toate instalațiile
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </a>
          </div>
        </div>

        <Lightbox
          open={lightbox.open}
          src={GALLERY_IMAGES[lightbox.index]}
          onClose={() => setLightbox({ open: false, index: 0 })}
          onPrev={() => setLightbox((s) => ({ open: true, index: (s.index - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length }))}
          onNext={() => setLightbox((s) => ({ open: true, index: (s.index + 1) % GALLERY_IMAGES.length }))}
        />
      </section>

      {/* About Section */}
      <section className="border-t bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900">Experiență și Profesionalism</h2>
              <p className="mt-3 text-slate-700">
                Activăm în domeniul instalațiilor din 1998, iar firma PFA Pocei Marius-Gheorghe a fost înființată în 2011. 
                Oferim servicii complete pentru locuințe și spații comerciale din Arad și împrejurimi.
              </p>
              <p className="mt-3 text-slate-700">
                Lucrăm cu echipamente moderne și materiale de calitate, asigurând durabilitatea instalațiilor. 
                Fiecare proiect beneficiază de consultanță gratuită, execuție profesională și service post-montaj.
              </p>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900">Zonă Deservită</h2>
              <p className="mt-3 text-slate-700">
                Deservim <strong>Arad și localitățile din împrejurimi</strong> cu toate tipurile de instalații sanitare și termice. 
                Intervenim rapid și oferim consultanță tehnică gratuită.
              </p>
              <p className="mt-3 text-slate-700">
                <strong>Contact:</strong><br />
                Telefon: <strong>0744 476 911</strong><br />
                Email: <strong>marius.pocei@gmail.com</strong><br />
                Program: Luni-Vineri 08:00-18:00, Sâmbătă 09:00-14:00
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="scroll-mt-28 border-t bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contact card */}
            <div className="rounded-2xl border bg-white p-6 shadow-sm">
              <h3 className="text-xl font-semibold md:text-2xl">Contact</h3>
              <p className="mt-2 text-slate-700">Suntem disponibili pentru orice tip de instalații sanitare în Arad și împrejurimi.</p>

              <div className="mt-5 grid gap-2">
                <ContactItem href="tel:+40744476911" label="+40 744 476 911" aria="Sună acum">
                  <IconPhone />
                </ContactItem>

                <ContactItem
                  href="https://wa.me/40744476911"
                  label="WhatsApp"
                  aria="Deschide conversația pe WhatsApp"
                  external
                >
                  <IconWhatsAppBlue />
                </ContactItem>

                <ContactItem
                  href="mailto:marius.pocei@gmail.com"
                  label="marius.pocei@gmail.com"
                  aria="Trimite email"
                >
                  <IconEmail />
                </ContactItem>

                <ContactItem label="Arad, România">
                  <IconPin />
                </ContactItem>

                <ContactItem label="PFA Pocei Marius-Gheorghe">
                  <IconBuilding />
                </ContactItem>
              </div>

              <div className="mt-6">
                <a
                  href="/contact"
                  className="block rounded-xl bg-green-600 px-5 py-3 text-center font-medium text-white shadow-sm transition hover:bg-green-700"
                >
                  Solicită o ofertă
                </a>
              </div>
            </div>

            {/* Why us card */}
            <div className="rounded-2xl border bg-sky-50 p-6 shadow-sm">
              <h4 className="text-lg font-semibold text-sky-900">Avantaje</h4>
              <ul className="mt-3 grid gap-2 text-sm text-sky-900/90">
                <Bullet>Lucrări garantate</Bullet>
                <Bullet>Materiale de calitate</Bullet>
                <Bullet>Intervenții rapide</Bullet>
                <Bullet>Consultanță gratuită</Bullet>
                <Bullet>Prețuri corecte</Bullet>
              </ul>
              <img
                src="/instalatii/IMG-20251020-WA0020.jpg"
                alt="Instalații sanitare de calitate"
                className="mt-4 aspect-video w-full rounded-xl object-cover shadow"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

/* -------------------- UI Subcomponents -------------------- */

function HeaderWithIcon({ icon, title }) {
  return (
    <div className="inline-flex items-center gap-3">
      <div className="rounded-xl border bg-sky-50 p-3 text-sky-700">{icon}</div>
      <h3 className="text-lg font-semibold md:text-xl">{title}</h3>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="inline-flex items-start gap-2">
      <CheckIcon className="mt-1 h-5 w-5 shrink-0 text-sky-600" />
      <span>{children}</span>
    </li>
  );
}

function ContactItem({ href, label, children, external = false, aria }) {
  const Comp = href ? "a" : "div";
  const props = href ? { href, ...(external ? { target: "_blank", rel: "noreferrer" } : {}), "aria-label": aria || label } : {};
  return (
    <Comp {...props} className="group inline-flex items-center gap-3 rounded-xl p-2 pr-3 transition hover:bg-sky-50">
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-sky-100 text-sky-700">{children}</span>
      <span className="text-sm text-slate-800 group-hover:text-slate-900">{label}</span>
    </Comp>
  );
}

function Lightbox({ open, src, onClose, onPrev, onNext }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/80" aria-modal="true" role="dialog" onClick={onClose}>
      <button
        aria-label="Închide"
        className="absolute right-4 top-4 rounded-lg bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <div className="flex h-full items-center justify-center px-3" onClick={(e) => e.stopPropagation()}>
        <button
          className="mr-2 hidden rounded-lg bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20 md:block"
          aria-label="Anterior"
          onClick={onPrev}
        >
          <svg width="22" height="22" viewBox="0 0 24 24">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          </svg>
        </button>

        <img src={src} alt="Imagine mărită" className="max-h-[80vh] w-auto max-w-[90vw] rounded-2xl object-contain shadow-2xl" />

        <button
          className="ml-2 hidden rounded-lg bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20 md:block"
          aria-label="Următor"
          onClick={onNext}
        >
          <svg width="22" height="22" viewBox="0 0 24 24">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          </svg>
        </button>
      </div>

      {/* Mobile next/prev */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-4 md:hidden">
        <button className="rounded-lg bg-white/10 px-4 py-2 text-white backdrop-blur transition hover:bg-white/20" onClick={(e) => { e.stopPropagation(); onPrev(); }}>
          Anterior
        </button>
        <button className="rounded-lg bg-white/10 px-4 py-2 text-white backdrop-blur transition hover:bg-white/20" onClick={(e) => { e.stopPropagation(); onNext(); }}>
          Următor
        </button>
      </div>
    </div>
  );
}

/* -------------------- Icons -------------------- */

function CheckIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Service icons */
function IconBoiler({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="6" y="4" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="10" r="2" stroke="currentColor" strokeWidth="2" />
      <path d="M9 14h6M9 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M6 18v2a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-2" stroke="currentColor" strokeWidth="2" />
      <path d="M12 4V2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconRadiator({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" rx="1" stroke="currentColor" strokeWidth="2" />
      <path d="M7 6v12M10 6v12M14 6v12M17 6v12" stroke="currentColor" strokeWidth="2" />
      <path d="M4 9h16M4 12h16M4 15h16" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function IconBathroom({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 8h16a2 2 0 0 1 2 2v6a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4v-6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" />
      <path d="M6 8V6a2 2 0 0 1 2-2h1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M6 20v1M18 20v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconTools({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Contact & social icons */
function IconPhone({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 16.5v2a2.5 2.5 0 0 1-2.7 2.5 16.8 16.8 0 0 1-7.3-3.1 16.6 16.6 0 0 1-5.9-5.9A16.8 16.8 0 0 1 2 4.7 2.5 2.5 0 0 1 4.5 2h2A1.5 1.5 0 0 1 8 3.2c.2 1 .6 2 .9 2.8.3.8.1 1.6-.4 2.1L7 9.7a13.8 13.8 0 0 0 7.3 7.3l1.6-1.5c.5-.5 1.3-.7 2.1-.4.9.3 1.8.7 2.8.9A1.5 1.5 0 0 1 21 16.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPin({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 21s7-4.2 7-10a7 7 0 1 0-14 0c0 5.8 7 10 7 10Z" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="11" r="2.4" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function IconBuilding({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="6" y="4" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M4 20h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconWhatsAppBlue({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" role="img">
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        fill="#FFFFFF"
        d="M16.14 13.22c-.21-.1-1.24-.61-1.43-.68-.19-.07-.33-.1-.47.1s-.54.68-.66.82c-.12.14-.24.15-.45.05-.21-.1-.9-.33-1.72-1.05-.64-.55-1.08-1.22-1.21-1.43-.13-.21-.01-.33.09-.43.1-.1.21-.24.31-.36.1-.12.14-.21.21-.35.07-.14.03-.26-.02-.36-.05-.1-.47-1.13-.64-1.55-.17-.41-.34-.35-.47-.36l-.4-.01c-.14 0-.36.05-.55.26-.19.21-.73.71-.73 1.73s.75 2 .86 2.14c.1.14 1.49 2.28 3.62 3.2 2.13.92 2.13.62 2.51.6.38-.02 1.24-.5 1.41-.98.17-.48.17-.89.12-.98-.05-.09-.19-.14-.4-.24Z"
      />
    </svg>
  );
}

function IconHeatPump({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      <path d="M12 9v6M9 12h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4M16 6V4M8 18v2M16 18v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconSolar({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <rect x="6" y="14" width="12" height="4" rx="1" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function IconEmail({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
